import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useStore, useSelector } from 'react-redux'
import useInactivityTimer from '@chegg-wt/core/hooks/useInactivityTimer'
import {
  useQueryStringQAParams,
  intOrUndefined,
  sessionTimeoutKey,
} from '@chegg-wt/core/hooks/useQueryStringQAParams'
import {
  modalSeen,
  StorageType,
} from '@chegg-wt/core/components/Modals/ExitPopupCfcModals/utils'
import { getRouteNameFromHref } from '../utils/getRouteNameFromHref'
import initRio from '../state/instrumentation/rio/utils/initRio'
import { setInitRio } from '../state/instrumentation/actions'
import { isRioInitSelector } from '../state/instrumentation/selectors'
import { routeToAdzoneMap } from '../constants/adzone'
import { AppRouteCreators, AppRouteName, GetFromQuery } from '../types/core'
import { setAdzone, locationChange, setAdLayoutName } from '../state/app/actions'
import { initializedSelector } from '../state/app/selectors'
import { layoutNameFromWidth } from '../utils/adframe'

export interface UseOnPageLoadProps {
  routes: AppRouteCreators
  getSourceFromQuery?: GetFromQuery
  getStyleFromQuery?: GetFromQuery
  getActualStyleId?: (style: string) => string
}

export interface UseOnPageLoadResult {
  routeName: AppRouteName
}

const useOnPageLoad = ({
  routes,
  getSourceFromQuery,
  getStyleFromQuery,
  getActualStyleId,
}: UseOnPageLoadProps): UseOnPageLoadResult => {
  const dispatch = useDispatch()
  const router = useRouter()
  const state = useStore().getState()
  const isRioInit = useSelector(isRioInitSelector)
  const initialized = useSelector(initializedSelector)

  const initialRouteName = getRouteNameFromHref({
    browserURL: router.asPath,
    router,
    routes,
    getSourceFromQuery,
    getStyleFromQuery,
    getActualStyleId,
  })

  const [routeName, setRouteName] = useState<AppRouteName>(initialRouteName)

  useEffect(() => {
    const updatedRouteName = getRouteNameFromHref({
      browserURL: router.asPath,
      router,
      routes,
      getSourceFromQuery,
      getStyleFromQuery,
      getActualStyleId,
    })

    const adzone = routeToAdzoneMap[updatedRouteName]
    dispatch(setAdzone(adzone))
    const adLayout = layoutNameFromWidth(window.innerWidth)
    dispatch(setAdLayoutName(adLayout))
    dispatch(locationChange({ routeName: updatedRouteName }))
    setRouteName(updatedRouteName)
  }, [router.asPath])

  // useEffect(() => {
  //   const isHome = router.route === '/'
  //   const isNewHomePage =
  //     isHome && isWritingAssistantHomeFlagEnabled && Config.siteName === CM_PRODUCT_NAME
  //   if (isNewHomePage) dispatch(setAdzone(Adzones.empty)) // ? dont show takeover reverse L shape ad on new eb homepage
  // }, [isWritingAssistantHomeFlagEnabled])

  useEffect(() => {
    if (initialized && !isRioInit) {
      initRio({ routeName, state })
      dispatch(setInitRio())
    }
  }, [initialized, isRioInit])

  // When the user has been inactive for 30 minutes, clear their modal seen state in sessionStorage
  // to QA with shorter inactivity times, use query string e.g. ?session-timeout=5000
  const queryStringVal = intOrUndefined(useQueryStringQAParams()[sessionTimeoutKey])
  const sessionTimeOut = queryStringVal ?? 30 * 60 * 1000

  useInactivityTimer(() => modalSeen(StorageType.sessionStorage).clear(), sessionTimeOut)

  return { routeName }
}

export default useOnPageLoad
